/**
 * "Invited by a friend?" — first open after installing from an invite link.
 * One tap on Paste reads the invite the web page copied, and joins the group.
 * Typing the 8-character code works too. See lib/invite-handoff.ts.
 */
import { useState } from "react";
import { ClipboardPaste, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { track } from "@/lib/analytics";
import { acceptInvite, extractInviteCode, readInviteFromClipboard, clearPendingInvite } from "@/lib/invite-handoff";

export function InviteHandoffCard() {
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const join = async (c: string, via: "paste" | "typed") => {
    setBusy(true);
    setError(null);
    const r = await acceptInvite(c);
    if (r.ok) {
      clearPendingInvite();
      track("invite_handoff_joined", { via });
      await refreshUser();
      toast({ title: r.alreadyMember ? "You're already in this group" : "You're in!", description: "Taking you to the group." });
      window.location.hash = `#/groups/${r.groupId}`;
      return;
    }
    track("invite_handoff_failed", { via, status: r.status });
    setError(
      r.status === 410 && /revoked/i.test(r.message) ? "That invite link was switched off (revoked). Ask your friend to send a new one."
      : r.status === 410 ? "That invite has expired. Ask your friend to send a new link."
      : r.status === 404 ? "We couldn't find that invite. Check the code, or ask your friend to send the link again."
      : r.message,
    );
    setBusy(false);
  };

  const onPaste = async () => {
    const c = await readInviteFromClipboard();
    if (c) return join(c, "paste");
    setTyping(true);
    setError("No invite found on your clipboard. Type the code from your link instead.");
  };

  return (
    <Card className="p-5 space-y-3 border-primary/30" data-testid="invite-handoff">
      <div className="space-y-1">
        <p className="text-base font-semibold text-foreground">Invited by a friend?</p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Tap Paste to join their group. We copied your invite when you tapped Download.
        </p>
      </div>
      {!typing ? (
        <div className="grid gap-2">
          <Button type="button" size="lg" className="w-full" onClick={onPaste} disabled={busy} data-testid="invite-handoff-paste">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><ClipboardPaste className="w-4 h-4 mr-2" />Paste invite</>)}
          </Button>
          <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setTyping(true)} data-testid="invite-handoff-type">
            Type the code instead
          </button>
        </div>
      ) : (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const c = extractInviteCode(code);
            if (!c) { setError("Codes are 8 letters and numbers, like 3F9A21BC, or paste the whole link."); return; }
            void join(c, "typed");
          }}
        >
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Invite code or link"
            autoCapitalize="characters"
            autoCorrect="off"
            aria-label="Invite code or link"
            data-testid="invite-handoff-code"
          />
          <Button type="submit" disabled={busy || !code.trim()} data-testid="invite-handoff-join">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join"}
          </Button>
        </form>
      )}
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    </Card>
  );
}
